import React, { useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { Field, Form, Formik } from 'formik';
import InputMask from 'react-input-mask';
import * as Yup from 'yup';
import useApi from '../../utils/useApi';
import { fetchCnpjInfo } from '../../utils/brasilApi';

// VALIDAÇÃO E FILTRO PARA O MODAL DE FORNECEDOR
const supplierValidationSchema = Yup.object().shape({
  name: Yup.string().required('Nome é obrigatório'),
  contractType: Yup.string().required('Tipo de contrato é obrigatório').oneOf(['FIXO', 'AVULSO']),
});

// COMPONENTE DO MODAL DE CRIAÇÃO DE FORNECEDOR
const SupplierCreateModal = ({ open, onClose, initialName, onSuccess }) => {
  const [cnpjLoading, setCnpjLoading] = useState(false);
  const [cnpjError, setCnpjError] = useState('');
  const [createLoad, createInfo, setCreateInfo] = useApi({
    method: 'post',
    url: '/suppliers',
  });

  const modalInitialState = {
    name: initialName || '',
    pseudonym: '',
    cnpj: '',
    contractType: 'FIXO',
    city: '',
    neighborhood: '',
    street: '',
    houseNumber: '',
    whatsapp: '',
    email: '',
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <Formik
        initialValues={modalInitialState}
        validationSchema={supplierValidationSchema}
        enableReinitialize
        onSubmit={(values, { setSubmitting }) => {
          createLoad({
            data: values,
            onCompleted: (res) => {
              if (res.data) {
                onSuccess(res.data);
              }
              setSubmitting(false);
              setCreateInfo(null);
            },
            onError: (err) => {
              setSubmitting(false);
              setCreateInfo({
                error: err,
                message: 'Falha ao criar fornecedor',
              });
            },
          });
        }}
      >
        {({ errors, touched, isSubmitting, setFieldValue, values }) => (
          <Form>
            <DialogTitle>Novo Fornecedor</DialogTitle>
            <DialogContent>
              <Grid container spacing={2} style={{ paddingTop: 8 }}>
                <Grid item xs={12} md={6}>
                  <InputMask
                    value={values.cnpj || ''}
                    mask="99.999.999/9999-99"
                    onChange={(ev) => {
                      const rawValue = ev.target.value;
                      setFieldValue('cnpj', rawValue);
                      const clean = rawValue.replace(/\D/g, '');
                      if (clean.length === 14) {
                        setCnpjLoading(true);
                        setCnpjError('');
                        fetchCnpjInfo(clean)
                          .then((data) => {
                            if (data.razao_social) setFieldValue('name', data.razao_social);
                            if (data.nome_fantasia) setFieldValue('pseudonym', data.nome_fantasia);
                            if (data.municipio) setFieldValue('city', data.municipio);
                            if (data.bairro) setFieldValue('neighborhood', data.bairro);
                            if (data.logradouro) setFieldValue('street', data.logradouro);
                            if (data.numero) setFieldValue('houseNumber', data.numero);
                            if (data.ddd_telefone_1) {
                              setFieldValue('whatsapp', data.ddd_telefone_1.replace(/\D/g, ''));
                            } else if (data.ddd_telefone_2) {
                              setFieldValue('whatsapp', data.ddd_telefone_2.replace(/\D/g, ''));
                            }
                            if (data.email) setFieldValue('email', data.email);
                          })
                          .catch(() => {
                            setCnpjError('CNPJ não encontrado');
                          })
                          .finally(() => setCnpjLoading(false));
                      }
                    }}
                  >
                    {() => (
                      <TextField
                        label="CNPJ"
                        placeholder="00.000.000/0000-00"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        error={Boolean(errors.cnpj || cnpjError)}
                        helperText={
                          cnpjLoading
                            ? 'Buscando CNPJ...'
                            : cnpjError || (touched.cnpj && errors.cnpj) || 'Informe o CNPJ'
                        }
                        InputProps={{
                          endAdornment: cnpjLoading ? <CircularProgress size={20} /> : null,
                        }}
                      />
                    )}
                  </InputMask>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    name="name"
                    label="Nome *"
                    InputLabelProps={{ shrink: true }}
                    error={Boolean(errors.name && touched.name)}
                    helperText={touched.name && errors.name}
                    fullWidth
                    as={TextField}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    name="pseudonym"
                    label="Nome Fantasia"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    as={TextField}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={Boolean(errors.contractType && touched.contractType)}>
                    <InputLabel shrink id="supplier-contractType-label">Tipo de Contrato *</InputLabel>
                    <Field
                      name="contractType"
                      labelId="supplier-contractType-label"
                      as={Select}
                    >
                      <MenuItem value="FIXO">FIXO</MenuItem>
                      <MenuItem value="AVULSO">AVULSO</MenuItem>
                    </Field>
                    {errors.contractType && touched.contractType && (
                      <Typography variant="caption" color="error">{errors.contractType}</Typography>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field name="city" label="Cidade" InputLabelProps={{ shrink: true }} fullWidth as={TextField} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    name="neighborhood"
                    label="Bairro"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    as={TextField}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <Field
                    name="street"
                    label="Rua/Avenida"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    as={TextField}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Field
                    name="houseNumber"
                    label="Número"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    as={TextField}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    name="whatsapp"
                    label="WhatsApp"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    as={TextField}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    name="email"
                    label="E-mail"
                    type="email"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    as={TextField}
                  />
                </Grid>
                {createInfo.error && (
                  <Grid item xs={12}>
                    <Typography color="error">
                      {createInfo.message || 'Erro ao salvar.'}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} color="primary" disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={isSubmitting || createLoad.loading}
              >
                {isSubmitting ? 'Salvando...' : 'Salvar'}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default SupplierCreateModal;